import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from "@angular/core";
import { DataTypeModelKey } from "app/models/dataType.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { ProjectViewModel } from "app/models/projectView.model";
import { ProjectViewService } from "app/services/api/projectView.service";
import { BehaviorSubject } from "rxjs";

type DeviceViewItem = {
    id: string;
    name: string;
    entries: any[];
};

@Component({
    selector: "app-device-view-list",
    templateUrl: "./device-view-list.component.html",
    styleUrls: ["./device-view-list.component.scss"],
    standalone: false,
})
export class DeviceViewListComponent implements OnChanges, OnDestroy {
    @Input()
    device: ProjectDeviceModel;
    @Input()
    dataType: DataTypeModelKey;

    readonly projectViews$ = new BehaviorSubject<DeviceViewItem[]>([]);
    private editSubscription: any;

    constructor(private projectViewService: ProjectViewService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.device?.project || !this.dataType) {
            return;
        }

        if (changes["device"] || changes["dataType"]) {
            this.loadData();
        }

        if (!this.editSubscription) {
            const editStream: any = this.projectViewService.onEditProjectView;
            if (!editStream?.subscribe) {
                return;
            }

            this.editSubscription = editStream.subscribe(
                // At each modification, load it from the current cached list
                () => {
                    this.projectViews$.next(this.toDeviceViewItems(this.projectViewService.viewList));
                },
            );
        }
    }

    ngOnDestroy(): void {
        if (this.editSubscription?.unsubscribe) {
            this.editSubscription.unsubscribe();
        }
    }

    private loadData() {
        if (!this.device?.project) {
            return;
        }
        this.projectViewService.getAll(this.device.project).subscribe(
            (data) => {
                this.projectViews$.next(this.toDeviceViewItems(data));
            },
            (err) => console.log(err),
        );
    }

    private getView(id: string): ProjectViewModel {
        return this.projectViews$.value.find((v) => v.id === id) as unknown as ProjectViewModel;
    }

    deviceInView(view: ProjectViewModel): boolean {
        if (!view?.entries || !this.device?.id) {
            return false;
        }
        return view.entries.some((e) => e.projectDeviceId === this.device.id && e.dataType === this.dataType);
    }

    clickView(viewId: string, event: any) {
        // Inhibit menu closing
        event.stopPropagation();

        const view = this.getView(viewId);
        if (!view || !this.device?.id || !this.device?.project) {
            return;
        }
        if (this.deviceInView(view)) {
            // remove
            const index = view.entries.findIndex((e) => e.projectDeviceId === this.device.id && e.dataType === this.dataType);
            if (index > -1) {
                view.entries.splice(index, 1);
            }
        } else {
            // add
            view.entries.push({
                projectDeviceId: this.device.id,
                dataType: this.dataType,
                cols: 1,
                rows: 1,
                x: 0,
                y: 0,
            });
        }

        this.projectViewService.edit(this.device.project, view).subscribe(null, (err) => console.log(err));
    }

    private toDeviceViewItems(data: ProjectViewModel[] | null | undefined): DeviceViewItem[] {
        return (data || []).map((view: ProjectViewModel) => ({
            id: view?.id || "",
            name: view?.name || "",
            entries: [...(view?.entries || [])],
        }));
    }
}
